"use client";

import { forwardRef, type Ref } from "react";

import { ReviewDialog } from "@/components/reviews/review-card";
import type { GoogleReview } from "@/lib/google-reviews";
import { useOpenWithFocus } from "@/lib/use-open-with-focus";

const TRUNCATE_AT = 140;

function formatName(name: string) {
  return name
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function Stars({ rating }: { rating: number }) {
  return (
    <div
      role="img"
      className="flex gap-0.5"
      aria-label={`${rating} out of 5 stars`}
    >
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          width={14}
          height={14}
          viewBox="0 0 20 20"
          className={
            i < Math.round(rating) ? "text-yellow-400" : "text-black/15"
          }
          fill="currentColor"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

type PatientReviewProps = {
  review: GoogleReview;
  tabIndex?: number;
  onFocus?: () => void;
};

const focusRing =
  "rounded-[8px] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-purple-04";

export const PatientReview = forwardRef<HTMLElement, PatientReviewProps>(
  function PatientReview({ review, tabIndex, onFocus }, ref) {
    const [open, setOpen] = useOpenWithFocus(false);
    const truncated = review.text.length > TRUNCATE_AT;
    const display = truncated
      ? `${review.text.slice(0, TRUNCATE_AT)}…`
      : review.text;

    const body = (
      <>
        <p className="text-button leading-[1.56] text-black/70">
          &ldquo;{display}&rdquo;
        </p>
        <span className="text-button font-bold text-black">
          {formatName(review.authorName)}
        </span>
        <Stars rating={review.rating} />
        {truncated && (
          <span className="text-caption text-purple-04 font-bold">
            Read more
          </span>
        )}
      </>
    );

    if (!truncated) {
      return (
        <div
          ref={ref as Ref<HTMLDivElement>}
          tabIndex={tabIndex}
          onFocus={onFocus}
          className={`flex flex-col gap-2 ${focusRing}`}
        >
          {body}
        </div>
      );
    }

    return (
      <>
        <button
          ref={ref as Ref<HTMLButtonElement>}
          type="button"
          tabIndex={tabIndex}
          onFocus={onFocus}
          onClick={() => setOpen(true)}
          aria-label={`Read full review from ${formatName(review.authorName)}`}
          className={`flex w-full cursor-pointer flex-col gap-2 text-left ${focusRing}`}
        >
          {body}
        </button>
        {open && <ReviewDialog review={review} onClose={() => setOpen(false)} />}
      </>
    );
  },
);
